/**
 * A landing-page testimonial, as the admin panel sees it.
 *
 * Not to be confused with `Review` (types/review.ts): a review is written by a
 * verified purchaser and moderated, whereas a testimonial is editorial copy
 * authored here. There is no status column — `is_active` alone decides whether
 * the storefront shows it.
 */
export interface Testimonial {
  id: string;
  customer_name: string;
  customer_city: string | null;
  cluster_label: string | null;
  quote: string;
  rating: number;
  /** SQLite has no boolean — 1 = shown on the landing page, 0 = hidden. */
  is_active: number;
  /** Lower shows first. The carousel's running order. */
  display_order: number;
  created_at: string;
  updated_at: string;
}

/** What the create/update endpoints accept. */
export interface TestimonialPayload {
  customer_name: string;
  customer_city: string | null;
  cluster_label: string | null;
  quote: string;
  rating: number;
  is_active: boolean;
}

/**
 * The six fragrance families the AI service clusters into.
 *
 * Mirrors the allowlist in the ecommerce service's routes/testimonials.ts, which
 * is the one that actually enforces it — this copy exists so the dropdown can't
 * offer a value the API would reject.
 */
export const CLUSTER_LABELS = [
  'Fresh & Citrus',
  'Woody & Musky',
  'Floral & Powdery',
  'Smoky & Resinous',
  'Gourmand & Sweet',
  'Herbal & Musky',
] as const;

export const QUOTE_MIN = 20;
export const QUOTE_MAX = 400;
export const NAME_MAX = 60;

/**
 * True for the three quotes seeded on a fresh deploy.
 *
 * They are fiction, written so the landing page's social-proof band is never an
 * empty section before the first real customer says anything — and they must be
 * replaced before launch. Their ids are fixed as `test-1..3` by the seeder; a
 * genuine testimonial's id is a UUID, so the prefix cannot collide.
 */
export function isPlaceholder(t: Testimonial): boolean {
  return /^test-\d+$/.test(t.id);
}
